"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useCartStore } from "@/lib/cart-store";

export function CartBadgeLink() {
  const totalItems = useCartStore((state) => state.totalItems());
  const [mounted, setMounted] = useState(false);
  const [isPopping, setIsPopping] = useState(false);
  const [showPlusOne, setShowPlusOne] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [showFlyIn, setShowFlyIn] = useState(false);
  const previousCountRef = useRef(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    if (totalItems > previousCountRef.current) {
      setIsPopping(false);
      setShowPlusOne(false);
      setShowFlyIn(false);

      const raf = window.requestAnimationFrame(() => {
        setIsPopping(true);
        setShowPlusOne(true);
        setIsShaking(true);
        setShowFlyIn(true);
      });

      const popTimeout = window.setTimeout(() => {
        setIsPopping(false);
      }, 520);

      const plusTimeout = window.setTimeout(() => {
        setShowPlusOne(false);
      }, 900);

      const shakeTimeout = window.setTimeout(() => {
        setIsShaking(false);
      }, 600);
      const flyTimeout = window.setTimeout(() => {
        setShowFlyIn(false);
      }, 760);

      previousCountRef.current = totalItems;

      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(popTimeout);
        window.clearTimeout(plusTimeout);
        window.clearTimeout(shakeTimeout);
        window.clearTimeout(flyTimeout);
      };
    }

    previousCountRef.current = totalItems;
  }, [mounted, totalItems]);

  const displayCount = mounted ? totalItems : 0;

  return (
    <Link
      href="/panier"
      className="hidden items-center gap-2 rounded-full border border-[var(--border)] bg-white/55 px-4 py-2 text-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--primary)] hover:bg-white/75 hover:text-[var(--primary)] sm:inline-flex"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className={`h-5 w-5 transition-transform duration-300 ${isShaking ? "animate-[cartShake_600ms_ease-in-out]" : ""}`}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.84l.383 1.437m0 0L6.75 14.25A2.25 2.25 0 009 16.5h7.5a2.25 2.25 0 002.18-1.704l1.2-4.5A2.25 2.25 0 0017.7 7.5H5.106m0 0L4.5 5.25M6.75 18a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm12 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
      <span className="sr-only">Panier</span>

      <span className="relative inline-flex items-center justify-center">
        {showFlyIn ? (
          <>
            <span className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--primary)]/10 blur-md animate-[cartFlyGlow_760ms_ease-out_forwards]" />
            <span className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-3 w-3 rounded-full bg-[var(--primary)] shadow-[0_0_18px_rgba(244,114,182,0.42)] animate-[cartFlyIn_760ms_cubic-bezier(0.22,1,0.36,1)_forwards]" />
            <span className="pointer-events-none absolute left-1/2 top-1/2 z-[9] h-[2px] w-10 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-[var(--primary)]/45 to-transparent animate-[cartFlyTrail_760ms_ease-out_forwards]" />
          </>
        ) : null}

        {showPlusOne ? (
          <span className="pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 animate-[cartPlusOne_900ms_ease-out_forwards] text-[10px] font-semibold text-[var(--primary)]">
            +1
          </span>
        ) : null}

        <span
          className={`relative z-20 inline-flex min-w-6 items-center justify-center rounded-full bg-[var(--primary)] px-2 py-0.5 text-[11px] font-medium text-white transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isPopping
              ? "scale-[1.22] shadow-[0_0_0_6px_rgba(244,114,182,0.10),0_14px_30px_rgba(244,114,182,0.28)] ring-2 ring-[var(--primary)]/35"
              : "scale-100 shadow-none ring-0"
          }`}
        >
          {displayCount}
        </span>
      </span>

      <style jsx>{`
        @keyframes cartPlusOne {
          0% {
            opacity: 0;
            transform: translate(-50%, 6px) scale(0.85);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, 0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -14px) scale(0.96);
          }
        }

        @keyframes cartShake {
          0% { transform: rotate(0deg); }
          20% { transform: rotate(-8deg); }
          40% { transform: rotate(6deg); }
          60% { transform: rotate(-4deg); }
          80% { transform: rotate(2deg); }
          100% { transform: rotate(0deg); }
        }

        @keyframes cartFlyIn {
          0% {
            opacity: 0;
            transform: translate(-42px, 12px) scale(0.7);
          }
          18% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate(-2px, -2px) scale(0.22);
          }
        }

        @keyframes cartFlyTrail {
          0% {
            opacity: 0;
            transform: translate(-42px, -50%) scaleX(0.45);
          }
          20% {
            opacity: 0.8;
          }
          100% {
            opacity: 0;
            transform: translate(-2px, -50%) scaleX(0.08);
          }
        }

        @keyframes cartFlyGlow {
          0% {
            opacity: 0;
            transform: translate(-42px, 12px) scale(0.6);
          }
          25% {
            opacity: 0.55;
          }
          100% {
            opacity: 0;
            transform: translate(-2px, -2px) scale(1.05);
          }
        }
      `}</style>
    </Link>
  );
}